
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { Journalist } from './types';

export const useProfileUpdate = (
  journalist: Journalist | null, 
  setJournalist: React.Dispatch<React.SetStateAction<Journalist | null>>,
  isOwnProfile: boolean
) => {
  const { user } = useAuth();
  
  const updateProfile = async (updates: Partial<Journalist>) => {
    if (!user || !isOwnProfile || !journalist) {
      toast({
        title: 'Permission denied',
        description: 'You can only edit your own profile',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      console.log('Updating profile with:', updates);
      
      // Convert the Journalist object structure to the database structure
      const profileUpdates: any = {};
      
      if (updates.name) profileUpdates.display_name = updates.name;
      if (updates.bio) profileUpdates.bio = updates.bio;
      
      // Map role to user_role with valid database values
      if (updates.role) {
        // Map frontend display roles to valid database roles
        // The database likely only accepts: 'reader', 'journalist', 'admin', etc.
        if (updates.role === 'Journalist') {
          profileUpdates.user_role = 'journalist';
        } else if (updates.role === 'Senior Correspondent') {
          // Still map to journalist since that's likely valid in the database
          profileUpdates.user_role = 'journalist';
        } else {
          // Default to reader if not a recognized role
          profileUpdates.user_role = 'reader';
        }
      }
      
      // Handle JSON fields with proper error checking
      if (updates.credentials) {
        try {
          profileUpdates.credentials = Array.isArray(updates.credentials) 
            ? JSON.stringify(updates.credentials) 
            : JSON.stringify([]);
        } catch (e) {
          console.error('Error stringifying credentials:', e);
          profileUpdates.credentials = '[]';
        }
      }
      
      if (updates.expertise) {
        try {
          profileUpdates.expertise = Array.isArray(updates.expertise) 
            ? JSON.stringify(updates.expertise) 
            : JSON.stringify([]);
        } catch (e) {
          console.error('Error stringifying expertise:', e);
          profileUpdates.expertise = '[]';
        }
      }
      
      if (updates.brandColor) profileUpdates.brand_color = updates.brandColor;
      if (updates.banner) profileUpdates.banner_url = updates.banner;
      
      // Update social fields
      if (updates.social) {
        if (updates.social.twitter) profileUpdates.twitter = updates.social.twitter;
        if (updates.social.linkedin) profileUpdates.linkedin = updates.social.linkedin;
        if (updates.social.facebook) profileUpdates.facebook = updates.social.facebook;
        if (updates.social.instagram) profileUpdates.instagram = updates.social.instagram;
        if (updates.social.website) profileUpdates.website = updates.social.website;
      }
      
      // Update subscription price
      if (updates.subscription?.price) {
        profileUpdates.subscription_price = updates.subscription.price;
      }
      
      console.log('Sending profile updates to Supabase:', profileUpdates);
      
      const { error, data } = await supabase
        .from('profiles')
        .update(profileUpdates)
        .eq('id', user.id)
        .select();
      
      if (error) {
        console.error('Supabase error details:', error);
        throw error;
      }
      
      console.log('Profile updated successfully:', data);
      
      // Update local state
      setJournalist({
        ...journalist,
        ...updates
      });
      
      toast({
        title: 'Profile updated',
        description: 'Your profile has been successfully updated'
      });
    } catch (error: any) {
      console.error('Error updating profile:', error);
      
      // Provide more specific error messages based on the error
      let errorMessage = 'There was a problem updating your profile';
      
      if (error.message) {
        if (error.message.includes('violates foreign key constraint')) {
          errorMessage = 'Invalid reference to another record';
        } else if (error.message.includes('violates check constraint')) {
          errorMessage = 'One of the values is invalid';
        } else if (error.message.includes('violates not-null constraint')) {
          errorMessage = 'A required field is missing';
        } else if (error.message.includes('duplicate key value')) {
          errorMessage = 'This value already exists';
        } else {
          // Include part of the original error for debugging
          errorMessage += `: ${error.message.substring(0, 50)}${error.message.length > 50 ? '...' : ''}`;
        }
      }
      
      toast({
        title: 'Update failed',
        description: errorMessage,
        variant: 'destructive'
      });
    }
  };
  
  return { updateProfile };
};
